/* eslint-disable @typescript-eslint/no-unused-vars */
import { Tabs, TabsProps } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getAccountInfo, getOrderList } from '../../../../api';
import { AutoTable } from '../../../../components/auto-table';
import getFormatMessage from '../../../../locales/get-format-message';
import {
  IApiOrderListItem,
  OrderStatus,
  OrderType,
} from '../../../../model/api-modules';
import { IRootState } from '../../../../store/reducers';
import { OrderStatusConfig } from '../../../studio/lib/service-order';
import { CashLackModal, OrderCancelModal, OrderPayModal } from './order-modal';
import styles from './index.module.scss';
import { fetchEnvCollectionList } from '../../../../api/game/env';
import { IApiGameEnvCollectionRes } from '../../../../model/api-modules/env';
import { OrderFilter } from './filter';
import { IFilterValues } from '../../../../model/billing-center';
import moment from 'moment';
import { getLocalTime, getPrice } from '../../../../lib';
import { CommonButton } from '../../../../components/button/main';
import { PermissionCheck } from '../../permission-check';
import { RbacRoleObjectKey } from '../../../../model';

export const OrderManage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<IFilterValues>({});
  const [list, setList] = useState<readonly IApiOrderListItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [gameEnvs, setGameEnvs] = useState<readonly IApiGameEnvCollectionRes[]>(
    []
  );
  const [showPayModal, setShowPayModal] = useState<boolean>(false);
  const [showCashLackModal, setShowCashLackModal] = useState<boolean>(false);
  const [cashRemain, setCashRemain] = useState<number>(0);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<IApiOrderListItem>();
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );

  useEffect(() => {
    init();
  }, [selectStudio?.studioID]);

  const init = async (withLoading = true) => {
    if (!selectStudio) {
      return;
    }
    setLoading(withLoading);
    const list = await getOrderList(selectStudio.studioID);
    const gameEnvRes = await fetchEnvCollectionList(selectStudio.studioID);
    if (gameEnvRes) {
      setGameEnvs(gameEnvRes);
    }
    if (list) {
      setList(list);
    }
    setLoading(false);
  };

  const showDetail = async (row: IApiOrderListItem) => {
    history.push(
      `/studio/${selectStudio?.studioID}/fee/order-manage/detail/${row.order_id}`
    );
  };

  const getFilterList = () => {
    const { dateRange, status, searchText } = filterValues;
    return list
      .filter((item) => !status || item.order_status === status)
      .filter(
        (item) =>
          !searchText ||
          item.order_id.includes(searchText) ||
          item.order_creator.includes(searchText)
      )
      .filter((item) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          return true;
        }
        const createTime = moment(item.create_time);
        return (
          createTime.isSameOrAfter(dateRange[0].startOf('day')) &&
          createTime.isSameOrBefore(dateRange[1].endOf('day'))
        );
      });
  };

  const renderContent = () => {
    if (!selectStudio) {
      return null;
    }
    const onChangedFilterValues = () => {};
    const filterList = getFilterList();
    const pageList = [...filterList].slice(
      (page - 1) * pageSize,
      page * pageSize
    );
    return (
      <>
        <OrderFilter
          onChange={(val) => {
            setFilterValues(val);
            setPage(1);
          }}
        />
        <AutoTable
          loading={loading}
          list={pageList}
          configFields={[
            {
              key: 'order_id',
              name: getFormatMessage('order_id'),
              tableExtension: {
                width: 236,
                renderRow(key, value, row) {
                  return (
                    <a onClick={() => showDetail(row as IApiOrderListItem)}>
                      {value}
                    </a>
                  );
                },
              },
            },
            {
              key: 'order_mode',
              name: getFormatMessage('order_fee_mode'),
              tableExtension: {
                renderRow(key, value) {
                  return getFormatMessage('order_post_fee');
                },
              },
            },
            {
              key: 'env_id',
              name: getFormatMessage('order_game'),
              tableExtension: {
                renderRow(key, value, row) {
                  const order = row as IApiOrderListItem;
                  const matchEnv = gameEnvs.find((env) =>
                    new RegExp(`${order.game_id}-\\d+-${order.env_id}`).test(
                      env.envGuid
                    )
                  );
                  if (matchEnv) {
                    return <span>{matchEnv.gameName}</span>;
                  }
                  return null;
                },
              },
            },
            {
              key: 'env_id',
              name: getFormatMessage('order_env'),
              tableExtension: {
                renderRow(key, value, row) {
                  const order = row as IApiOrderListItem;
                  const matchEnv = gameEnvs.find((env) =>
                    new RegExp(`${order.game_id}-\\d+-${order.env_id}`).test(
                      env.envGuid
                    )
                  );
                  if (matchEnv) {
                    return <span>{matchEnv.envName}</span>;
                  }
                  return null;
                },
              },
            },
            {
              key: 'order_type',
              name: getFormatMessage('order_type'),
              tableExtension: {
                renderRow(key, value) {
                  if (value === OrderType.New) {
                    return getFormatMessage('order_new_pay');
                  }
                  if (value === OrderType.Renew) {
                    return getFormatMessage('order_renew');
                  }
                },
              },
            },
            {
              key: 'create_time',
              name: getFormatMessage('order_create_time'),
              tableExtension: {
                renderRow(key, value) {
                  return getLocalTime(value);
                },
              },
            },
            {
              key: 'order_creator',
              name: getFormatMessage('order_creator'),
            },
            {
              key: 'order_status',
              name: getFormatMessage('order_status'),
              tableExtension: {
                renderRow(key, value) {
                  const config = OrderStatusConfig[value];
                  if (!config) {
                    return null;
                  }

                  return (
                    <div>
                      <span>{config.listIcon}</span>
                      <span style={{ color: config.textColor }}>
                        {config.name}
                      </span>
                    </div>
                  );
                },
              },
            },
            {
              key: 'freeze_amount',
              name: getFormatMessage('order_price'),
              tableExtension: {
                renderRow(key, value) {
                  return getPrice(value).amount;
                },
              },
            },
            {
              key: 'operate',
              name: getFormatMessage('operate'),
              tableExtension: {
                renderRow(key, value, row) {
                  const order = row as IApiOrderListItem;
                  const isCanPay = order.order_status === OrderStatus.LOADING;
                  const isCanCancel = [
                    OrderStatus.LOADING,
                    OrderStatus.REVIEW,
                  ].includes(order.order_status);

                  return (
                    <div className={styles.operateCell}>
                      <CommonButton
                        type="link"
                        onClick={() => showDetail(order)}
                      >
                        {getFormatMessage('fee_Details')}
                      </CommonButton>
                      {isCanPay && (
                        <PermissionCheck
                          permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                        >
                          <CommonButton
                            type="link"
                            onClick={async () => {
                              const accountInfo = await getAccountInfo(
                                selectStudio.studioID
                              );
                              const accountCash = Number(
                                accountInfo?.account_balance || 0
                              );

                              if (accountCash < 0) {
                                setShowCashLackModal(true);
                                setSelectedOrder(order);
                                setCashRemain(accountCash);
                                return;
                              }

                              setShowPayModal(true);
                              setSelectedOrder(order);
                            }}
                          >
                            {getFormatMessage('order_operate_pay')}
                          </CommonButton>
                        </PermissionCheck>
                      )}
                      {isCanCancel && (
                        <PermissionCheck
                          permissionKey={RbacRoleObjectKey.FeeCenterEdit}
                        >
                          <CommonButton
                            type="link"
                            onClick={() => {
                              setShowCancelModal(true);
                              setSelectedOrder(order);
                            }}
                          >
                            {getFormatMessage('cancel')}
                          </CommonButton>
                        </PermissionCheck>
                      )}
                    </div>
                  );
                },
              },
            },
          ]}
          tableExtension={{
            onChange: (changeInfo) => {
              if (!changeInfo) {
                return;
              }
              setPage(changeInfo.current);
              setPageSize(changeInfo.pageSize || 10);
            },
            pagination: {
              current: page,
              total: filterList.length,
              pageSize,
              showTotal: true,
            },
          }}
          layoutExtension={{
            showTopbar: false,
            showFilter: true,
          }}
          onChangedFilterValues={onChangedFilterValues}
        />
      </>
    );
  };

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: getFormatMessage('order_post'),
      children: renderContent(),
    },
  ];
  const cancelAllModal = () => {
    setShowCancelModal(false);
    setShowCashLackModal(false);
    setShowPayModal(false);
    setSelectedOrder(undefined);
    setCashRemain(0);
  };

  const refresh = () => {
    cancelAllModal();
    init(false);
  };

  const renderModal = () => {
    if (!selectedOrder) {
      return null;
    }

    return (
      <>
        {showPayModal && (
          <OrderPayModal
            order={selectedOrder}
            onOk={refresh}
            onCancel={cancelAllModal}
          />
        )}
        {showCancelModal && (
          <OrderCancelModal
            order={selectedOrder}
            onOk={refresh}
            onCancel={cancelAllModal}
          />
        )}

        {showCashLackModal && (
          <CashLackModal
            cashRemain={cashRemain}
            order={selectedOrder}
            onOk={refresh}
            onCancel={cancelAllModal}
          />
        )}
      </>
    );
  };
  return (
    <div className={styles.orderManagePage}>
      {/* <Tabs defaultActiveKey="1" items={tabItems} />*/}
      {renderContent()}
      {renderModal()}
    </div>
  );
};
